import { usePost } from "@albertine/api-client";
import {
    type ChatOutboxDraft,
    type ChatOutboxUpload,
    recordChatOutboxUploadId,
} from "../-data/chatOutbox";
import { getChatOutboxFile } from "../-data/chatOutboxFiles";

export function useChatAttachmentUploads() {
    const { mutateAsync: initiateFileUpload } = usePost("initiateFileUpload");
    const { mutateAsync: finalizeFileUpload } = usePost("finalizeFileUpload");

    async function resolveDraftUploads(
        draft: ChatOutboxDraft,
    ): Promise<ChatOutboxDraft> {
        if (draft.uploads.length === 0) {
            return draft;
        }

        const uploads: ChatOutboxUpload[] = [];
        for (const upload of draft.uploads) {
            uploads.push(await resolveUpload(draft.sendingId, upload));
        }

        return { ...draft, uploads };
    }

    async function resolveUpload(
        sendingId: string,
        upload: ChatOutboxUpload,
    ): Promise<ChatOutboxUpload> {
        if (upload.fileUploadId) {
            return upload;
        }

        const file = getChatOutboxFile(upload.localId);
        if (!file) {
            throw new Error("The attachment is no longer available on this device");
        }

        const initiated = await initiateFileUpload({
            json: {
                uploadType: "chat-message-attachment",
                fileName: upload.fileName,
                mimeType: upload.mimeType,
            },
        });

        const transfer = await fetch(initiated.uploadUrl, {
            method: initiated.uploadHttpMethod,
            headers: initiated.uploadHeaders,
            body: file,
        });
        if (!transfer.ok) {
            throw new Error(`Uploading the attachment failed (${transfer.status})`);
        }

        const finalized = await finalizeFileUpload({ param: { id: initiated.id } });
        if (finalized.status === "rejected") {
            throw new Error("The attachment was rejected");
        }

        recordChatOutboxUploadId(sendingId, upload.localId, initiated.id);

        return { ...upload, fileUploadId: initiated.id };
    }

    return { resolveDraftUploads };
}
